import i18next from 'i18next';
import { get, isArray, isEmpty, isObject, isString } from 'lodash';

import { DO_NOT_ASSIGN_VALUE } from 'common/constants';
import alerts from 'common/modules/alerts';
import { getUserPermits } from 'common/utils/helpers/session';
import showToast from 'common/utils/showToast';

const dataImageRegex =
    /^\s*data:([a-z]+\/[a-z]+(;[a-z-]+=[a-z-]+)?)?(;base64)?,[a-z0-9!$&',()*+,;=\-._~:@/?%\s]*\s*$/i;
const urlRegex =
    /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;
export const emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
export const phoneRegex =
    /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

const countDecimals = (value) => {
    const newValue = parseExponentialNumber(value);
    let decimalsNumber = 0;
    if (!isNaN(parseFloat(newValue))) {
        const parts = newValue.toString().split('.');
        if (parts[1]) decimalsNumber = parts[1].length;
        return decimalsNumber;
    }
    return 0;
};

const getDecimals = (value) => {
    if (!value) return 0;
    const decimals = countDecimals(value);
    if (decimals <= 1) return 1;
    if (decimals >= 2 && decimals <= 4) return decimals;
    return 4;
};

const parseExponentialNumber = (value) => {
    if (!value) return 0;
    if (!(value.toString().indexOf('e') !== -1)) return value;
    const parts = value.toString().split('e-');
    if (parts[1]) return value.toFixed(parts[1]);
};

const stringToColor = (string) => {
    let hash = 0;
    let color = '';
    for (let i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    for (let i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return `#${color}`;
};

export const arraytoDictionary = (array, key) => {
    if (!Array.isArray(array)) return {};
    return array.reduce(
        (acc, current) => ({ ...acc, [current[key]]: current }),
        {},
    );
};

export const capitalize = (s) => {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.slice(1);
};

export const dictionaryToArray = (dictionary) =>
    Object.keys(dictionary).map((key) => dictionary[key]);

export const getAgentOptions = (agents) => {
    const items = agents
        .filter((agent) => agent.is_active && agent.is_enabled)
        .map((item) => {
            return { label: item.full_name, optionValue: item.id };
        });
    return [
        {
            label: i18next.t('All text', { count: 2 }),
            optionValue: '',
        },
        {
            label: i18next.t('Without solar advisor'),
            optionValue: DO_NOT_ASSIGN_VALUE,
        },
        ...items,
    ];
};

export const getDataGridClasses = (isDarkMode) => ({
    '& .MuiDataGrid-row.even': { backgroundColor: '#f7fcff', color: '#000' },
    '& .MuiDataGrid-row--editing .MuiDataGrid-cell': {
        backgroundColor: 'rgba(255, 154, 0, 0.16)',
    },
    '& .MuiDataGrid-row--editing:hover .MuiDataGrid-cell': {
        backgroundColor: 'rgba(255, 154, 0, 0.16)',
    },
    '& .MuiInputBase-formControl input': {
        border: 0,
        outline: 'none',
        boxShadow: 'none',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: 'primary.main',
        color: '#fff',
    },
    '& .MuiDataGrid-iconButtonContainer .MuiIconButton-root .MuiSvgIcon-root': {
        fill: '#fff',
    },
    ...(isDarkMode && {
        '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'transparent !important',
            color: '#fff',
        },
        '& .MuiDataGrid-row.even': { backgroundColor: '#000', color: '#fff' },
        '& .MuiDataGrid-row.even .MuiLink-root': { color: '#fff' },
        '& .MuiDataGrid-row.odd .MuiLink-root': { color: '#fff' },
    }),
});

export const getFileNameByPath = (url) => {
    return url.substring(url.lastIndexOf('/') + 1);
};

export const getProductBrandLabel = (brand) => {
    if (!isEmpty(brand)) {
        return `${get(brand, 'name', '')} (${get(brand, 'country.name', '')})`;
    }
    return '';
};

export const getProjectStatusOptions = () => {
    return [
        {
            label: i18next.t('In process'),
            primary_color: '#34eb40',
            value: 'in_process',
        },
        {
            label: i18next.t('Cancelled'),
            primary_color: '#eb3434',
            value: 'cancelled',
        },
        {
            label: i18next.t('In review'),
            primary_color: '#ebe234',
            value: 'reviewed',
        },
        {
            label: i18next.t('Completed'),
            primary_color: '#d934eb',
            value: 'completed',
        },
        {
            label: i18next.t('Installed'),
            primary_color: '#346eeb',
            value: 'installed',
        },
    ];
};

export const getStatusProposalLabel = (isGenerated) => {
    if (isGenerated) return i18next.t('Generated', { context: 'female' });
    return i18next.t('Draft');
};

export const getSystemTypesOptions = (types) => {
    const items = types.map((item) => {
        return {
            label: item.name,
            value: item.id,
        };
    });

    return [
        {
            disabled: true,
            label: i18next.t('Select a type'),
            value: '',
        },
        ...items,
    ];
};

export const handleBuildTheme = ({ baseDarkPalette, baseLightPalette }) => {
    return {
        darkPalette: baseDarkPalette,
        lightPalette: baseLightPalette,
    };
};

export const handleFileURL = (url, preffix) => {
    const pattern = /^((https):\/\/)/;
    return pattern.test(url) ? url : `${preffix}${url}`;
};

export const isImageBase64 = (string) => string.match(dataImageRegex);

export const isImageUrl = (string) => string.match(urlRegex);

export const isJsonString = (str) => {
    try {
        JSON.parse(str);
    } catch (e) {
        return false;
    }
    return true;
};

export const numberFormat = (value, options) => {
    const newValue = parseExponentialNumber(value);
    const {
        currency,
        decimals = getDecimals(value),
        locale = 'en-US',
        unit = '',
        style,
    } = options;
    let formatted = newValue;
    let unitText = '';
    switch (style) {
        case 'currency':
            formatted = new Intl.NumberFormat(locale, {
                style,
                currency,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(newValue);
            break;
        case 'decimal':
            formatted = new Intl.NumberFormat(locale, {
                style,
                minimumFractionDigits: decimals,
                maximumFractionDigits: decimals,
            }).format(newValue);
            unitText = unit;
            break;
        default:
            formatted = newValue;
            break;
    }
    return `${formatted} ${!isEmpty(unitText) ? unitText : ''}`;
};

export const panelTypeToLabel = (panelType) => {
    const panelTypes = {
        0: i18next.t('Monocrystalline'),
        1: i18next.t('Polycrystalline'),
        2: i18next.t('Thin Film'),
    };
    return panelTypes[panelType];
};

export const showReponseErrorsAsAlert = (dispatch, response) => {
    let errors = [];
    const responseErrors = get(response, 'data.errors', null);
    if (responseErrors) {
        if (isArray(responseErrors))
            errors = responseErrors.map((error) => error);
        else if (isString(responseErrors)) errors = [responseErrors];
        else if (isObject(responseErrors)) {
            Object.keys(responseErrors).forEach((key) => {
                if (isArray(responseErrors[key])) {
                    errors = [...errors, `${key} ${responseErrors[key][0]}`];
                }
                if (isString(responseErrors[key])) {
                    errors = [...errors, responseErrors[key]];
                }
            });
        }
    }
    if (!isEmpty(errors)) {
        const dataAlert = {
            confirmText: i18next.t('Accept'),
            messages: errors,
            title: i18next.t('Error'),
            type: alerts.ALERT_TYPE_ALERT,
        };
        dispatch(alerts.actions.show(dataAlert));
    } else {
        showToast({
            type: 'error',
            body: i18next.t('An error occurred during the process'),
        });
    }
};

export const stringAvatar = (name) => {
    const nameArray = name.split(' ');
    let newName = '';
    if (nameArray.length === 1) newName = nameArray[0][0];
    else if (nameArray.length >= 2 && nameArray[1][0])
        newName = `${nameArray[0][0]}${nameArray[1][0]}`;
    return {
        sx: { bgcolor: stringToColor(name) },
        children: newName?.toUpperCase(),
    };
};

export const validateAccess = (requiredPermissions) => {
    const userPermits = getUserPermits();
    // Default to false if userPermits is null or not an array
    if (!Array.isArray(userPermits) || userPermits === null) {
        return false;
    }

    // If requiredPermissions is not an array or is empty, grant access
    if (
        !Array.isArray(requiredPermissions) ||
        requiredPermissions.length === 0
    ) {
        return true;
    }

    // Check if any required permission is in userPermits
    return requiredPermissions.some((value) => userPermits.includes(value));
};

export const nextItemsToSelect = (itemsSelected = [], itemsToSelect) =>
    itemsToSelect.filter(
        (itemToSelect) =>
            itemToSelect.value !== '' &&
            itemsSelected
                .map((itemSelected) => itemSelected.item)
                .indexOf(itemToSelect.value) < 0,
    );

export const itemsToSelectRHFWithoutDuplicates = (
    id,
    values,
    itemsToSelect,
) => {
    const selected = values.map((item) => item.item);
    const newItemsToSelect = itemsToSelect.map((item) => {
        if (
            selected.indexOf(item.value) === -1 ||
            item.value === id ||
            item.value === 'undefined' ||
            item.value === ''
        )
            return { ...item, disabled: item?.disabled || false };
        return { ...item, disabled: true };
    });
    newItemsToSelect[0].disabled = true;
    return newItemsToSelect;
};
